/**
 * MIQBaselineModal.js
 * 
 * Modal for displaying the MIQ Baseline Report (Day 30+)
 * Shows for Genius Plaite users only
 * 
 * Sections:
 * - Context Snapshot
 * - Habit Patterns
 * - Meal Timing Trends
 * - Macro Distribution Patterns
 * - Weight Trend Trajectory
 * - SmartMeals Impact
 * - SmartSwaps Impact
 * - SmartHub Logging Strength
 * - Action Guidance
 */

import React from 'react';
import { X, User, Clock, PieChart, TrendingUp, Utensils, ChefHat, Star, Lightbulb, Shield } from 'lucide-react';
import { brand } from '../../styles/brand';
import { track } from '../../utils/analytics';

const { colors, gradients, typography, radius, shadows, spacing } = brand;

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const ReportSection = ({ icon: Icon, title, children, color = '#667eea' }) => (
  <div style={{
    background: colors.panel,
    border: `1px solid ${colors.stroke}`,
    borderRadius: radius.lg,
    padding: spacing.s4,
    marginBottom: spacing.s3
  }}>
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: spacing.s2,
      marginBottom: spacing.s3
    }}>
      <Icon size={18} style={{ color }} />
      <span style={{ 
        fontSize: typography.body.fontSize, 
        fontWeight: '600',
        color: colors.text
      }}>
        {title}
      </span>
    </div>
    {children}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// BASELINE READY MODAL (Shown once when Day 30 reached)
// ═══════════════════════════════════════════════════════════════════════════════
export const BaselineReadyModal = ({ isOpen, onClose, onViewReport, isGeniusTier, onUpgrade }) => {
  if (!isOpen) return null;

  const handlePrimaryAction = () => {
    if (isGeniusTier) {
      track('miq_baseline_viewed', { source: 'modal' });
      onViewReport?.();
    } else {
      track('miq_upgrade_clicked_from_miq', { context: 'baseline_ready_modal' });
      onUpgrade?.();
    }
    onClose?.();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10001,
      padding: spacing.s4
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        borderRadius: radius.xl,
        padding: spacing.s6,
        maxWidth: '400px',
        width: '100%',
        textAlign: 'center',
        border: '1px solid rgba(102, 126, 234, 0.3)',
        boxShadow: '0 20px 60px rgba(102, 126, 234, 0.2)'
      }}>
        {/* Celebration Icon */}
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          boxShadow: '0 10px 30px rgba(102, 126, 234, 0.4)'
        }}>
          <span style={{ fontSize: '36px' }}>🧠</span>
        </div>

        <h2 style={{
          fontSize: '22px',
          fontWeight: '700',
          color: colors.text,
          margin: '0 0 12px 0'
        }}>
          Your MIQ Baseline is Ready
        </h2>

        <p style={{
          fontSize: typography.body.fontSize,
          color: colors.textSecondary,
          margin: '0 0 24px 0',
          lineHeight: 1.5
        }}>
          MIQ has established your metabolic baseline from 30 days of patterns.
        </p>

        <button
          onClick={handlePrimaryAction}
          style={{
            width: '100%',
            padding: `${spacing.s3} ${spacing.s4}`,
            background: isGeniusTier 
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            border: 'none',
            borderRadius: radius.md,
            color: isGeniusTier ? 'white' : '#1a1a2e',
            fontSize: typography.body.fontSize,
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: spacing.s3
          }}
        >
          {isGeniusTier ? 'View Baseline Report' : 'Upgrade to Genius Plaite'}
        </button>

        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: colors.textSecondary,
            fontSize: typography.bodySmall.fontSize,
            cursor: 'pointer'
          }}
        >
          {isGeniusTier ? 'View Later' : 'Maybe Later'}
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN BASELINE REPORT VIEW
// ═══════════════════════════════════════════════════════════════════════════════
const MIQBaselineModal = ({ 
  isOpen, 
  onClose, 
  baselineData = {},
  userProfile = {},
  aiNutritionGoals = {}
}) => {
  if (!isOpen) return null;

  // Extract data with defaults
  const {
    habitPatterns = {},
    mealTimingTrends = {},
    macroDistribution = {},
    weightTrend = {},
    smartMealsImpact = {},
    smartSwapsImpact = {},
    smartHubStrength = {},
    actionGuidance = []
  } = baselineData;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
      zIndex: 10002,
      overflowY: 'auto'
    }}>
      {/* Header */}
      <div style={{
        position: 'sticky',
        top: 0,
        background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.95) 0%, rgba(22, 33, 62, 0.95) 100%)',
        backdropFilter: 'blur(10px)',
        padding: spacing.s4,
        borderBottom: `1px solid ${colors.stroke}`,
        zIndex: 10
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between'
        }}>
          <div>
            <h1 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: colors.text,
              margin: 0
            }}>
              MIQ Baseline Report
            </h1>
            <p style={{
              fontSize: typography.bodySmall.fontSize,
              color: colors.textSecondary,
              margin: '4px 0 0 0'
            }}>
              Your 30-Day Metabolic Intelligence Analysis
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: radius.full,
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: colors.text
            }}
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: spacing.s4 }}>
        {/* Context Snapshot */}
        <ReportSection icon={User} title="Context Snapshot" color="#667eea">
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: spacing.s3 
          }}>
            <div style={{ 
              background: 'rgba(255,255,255,0.05)', 
              padding: spacing.s3, 
              borderRadius: radius.md 
            }}>
              <span style={{ fontSize: '11px', color: colors.textSecondary }}>Health Context</span>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: colors.text }}>
                {userProfile?.medical_conditions?.length > 0 
                  ? userProfile.medical_conditions.slice(0, 2).join(', ')
                  : 'Not specified'}
              </p>
            </div>
            <div style={{ 
              background: 'rgba(255,255,255,0.05)', 
              padding: spacing.s3, 
              borderRadius: radius.md 
            }}>
              <span style={{ fontSize: '11px', color: colors.textSecondary }}>Primary Goal</span>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: colors.text }}>
                {userProfile?.primary_goal || aiNutritionGoals?.intake_data?.primary_goal || 'Not set'}
              </p>
            </div>
            <div style={{ 
              background: 'rgba(255,255,255,0.05)', 
              padding: spacing.s3, 
              borderRadius: radius.md 
            }}>
              <span style={{ fontSize: '11px', color: colors.textSecondary }}>Ancestry</span>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: colors.text }}>
                {aiNutritionGoals?.intake_data?.ancestry || 'Not specified'}
              </p>
            </div>
            <div style={{ 
              background: 'rgba(255,255,255,0.05)', 
              padding: spacing.s3, 
              borderRadius: radius.md 
            }}>
              <span style={{ fontSize: '11px', color: colors.textSecondary }}>Daily Target</span>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: colors.text }}>
                {aiNutritionGoals?.goals?.calories || userProfile?.calorie_goal || 2000} kcal
              </p>
            </div>
          </div>
        </ReportSection>

        {/* Habit Patterns */}
        <ReportSection icon={Clock} title="Habit Patterns" color="#38ef7d">
          <p style={{ 
            fontSize: typography.bodySmall.fontSize, 
            color: colors.textSecondary,
            margin: 0 
          }}>
            {habitPatterns.summary || 'Your eating patterns are being analyzed. Continue logging for more insights.'}
          </p>
          {habitPatterns.consistency && (
            <div style={{ 
              marginTop: spacing.s3,
              display: 'flex',
              alignItems: 'center',
              gap: spacing.s2
            }}>
              <span style={{ fontSize: '11px', color: colors.textSecondary }}>Logging Consistency:</span>
              <span style={{ 
                fontSize: '13px', 
                fontWeight: '600',
                color: habitPatterns.consistency > 70 ? '#38ef7d' : habitPatterns.consistency > 40 ? '#FFD700' : '#ff6b6b'
              }}>
                {habitPatterns.consistency}%
              </span>
            </div>
          )}
        </ReportSection>

        {/* Meal Timing Trends */}
        <ReportSection icon={Clock} title="Meal Timing Trends" color="#FF9800">
          <p style={{ 
            fontSize: typography.bodySmall.fontSize, 
            color: colors.textSecondary,
            margin: 0 
          }}>
            {mealTimingTrends.summary || 'Meal timing analysis will appear as more data is collected.'}
          </p>
        </ReportSection>

        {/* Macro Distribution */}
        <ReportSection icon={PieChart} title="Macro Distribution Patterns" color="#9333ea">
          <p style={{ 
            fontSize: typography.bodySmall.fontSize, 
            color: colors.textSecondary,
            margin: 0 
          }}>
            {macroDistribution.summary || 'Your macronutrient balance is being tracked.'}
          </p>
        </ReportSection>

        {/* Weight Trend */}
        <ReportSection icon={TrendingUp} title="Weight Trend Trajectory" color="#00b894">
          <p style={{ 
            fontSize: typography.bodySmall.fontSize, 
            color: colors.textSecondary,
            margin: 0 
          }}>
            {weightTrend.summary || 'Log weight check-ins to see your trajectory here.'}
          </p>
        </ReportSection>

        {/* SmartMeals Impact */}
        <ReportSection icon={Utensils} title="SmartMeals Impact" color="#667eea">
          <p style={{ 
            fontSize: typography.bodySmall.fontSize, 
            color: colors.textSecondary,
            margin: 0 
          }}>
            {smartMealsImpact.summary || 'Use SmartMeals to see AI-generated meal suggestions impact.'}
          </p>
        </ReportSection>

        {/* SmartSwaps Impact */}
        <ReportSection icon={ChefHat} title="SmartSwaps Impact" color="#764ba2">
          <p style={{ 
            fontSize: typography.bodySmall.fontSize, 
            color: colors.textSecondary,
            margin: 0 
          }}>
            {smartSwapsImpact.summary || 'Use SmartSwaps to see healthy recipe alternatives impact.'}
          </p>
        </ReportSection>

        {/* SmartHub Strength */}
        <ReportSection icon={Star} title="SmartHub Logging Strength" color="#FFD700">
          <p style={{ 
            fontSize: typography.bodySmall.fontSize, 
            color: colors.textSecondary,
            margin: 0 
          }}>
            {smartHubStrength.summary || 'Save and log from SmartHub to track your favorite healthy meals.'}
          </p>
        </ReportSection>

        {/* Action Guidance */}
        <ReportSection icon={Lightbulb} title="Action Guidance" color="#38ef7d">
          {actionGuidance.length > 0 ? (
            <ul style={{ 
              margin: 0, 
              paddingLeft: '20px',
              color: colors.textSecondary,
              fontSize: typography.bodySmall.fontSize
            }}>
              {actionGuidance.map((action, index) => (
                <li key={index} style={{ marginBottom: '8px' }}>{action}</li>
              ))}
            </ul>
          ) : (
            <ul style={{ 
              margin: 0, 
              paddingLeft: '20px',
              color: colors.textSecondary,
              fontSize: typography.bodySmall.fontSize
            }}>
              <li style={{ marginBottom: '8px' }}>Continue logging meals consistently for better insights</li>
              <li style={{ marginBottom: '8px' }}>Use SmartSwaps to discover healthier alternatives</li>
              <li style={{ marginBottom: '8px' }}>Complete daily check-ins to track your patterns</li>
              <li style={{ marginBottom: '8px' }}>Save favorite healthy recipes to SmartHub</li>
              <li style={{ marginBottom: '8px' }}>Log weight weekly to see your trajectory</li>
            </ul>
          )}
        </ReportSection>

        {/* Disclaimer */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: `1px solid ${colors.stroke}`,
          borderRadius: radius.md,
          padding: spacing.s3,
          display: 'flex',
          alignItems: 'flex-start',
          gap: spacing.s2,
          marginTop: spacing.s4
        }}>
          <Shield size={16} style={{ color: colors.textSecondary, flexShrink: 0, marginTop: '2px' }} />
          <p style={{
            fontSize: '11px',
            color: colors.textSecondary,
            margin: 0,
            lineHeight: 1.4
          }}>
            MIQ provides personalized pattern insights and does not diagnose or treat medical conditions.
            Always consult healthcare professionals for medical advice.
          </p>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: spacing.s3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            borderRadius: radius.md,
            color: 'white',
            fontSize: typography.body.fontSize,
            fontWeight: '600',
            cursor: 'pointer',
            marginTop: spacing.s4
          }}
        >
          Close Report
        </button>
      </div>
    </div>
  );
};

export default MIQBaselineModal;
